import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface IClientNotification {
  id: number;
  message: string;
  isRead: boolean;
  createdAt: string;
  type: string | null;
}

/**
 * Reads the customer's in-app notifications from BC_Identity (e.g. the outcome
 * of an introducer request). Kept reactive so the header bell can show a live
 * unread count.
 */
@Injectable({ providedIn: 'root' })
export class ClientNotificationService {
  private readonly httpClient = inject(HttpClient);
  private readonly url = environment.apiConfig.IdentityAPI.endpoint;

  private readonly $notifications = new BehaviorSubject<IClientNotification[]>([]);
  readonly notifications$: Observable<IClientNotification[]> = this.$notifications.asObservable();

  private readonly $unreadCount = new BehaviorSubject<number>(0);
  readonly unreadCount$: Observable<number> = this.$unreadCount.asObservable();

  /** Fetch the latest notifications and push them into the reactive state. */
  refresh(): void {
    this.httpClient
      .get<IClientNotification[]>(`${this.url}/customer/account/notifications`)
      .subscribe({
        next: (items) => this.setNotifications(items ?? []),
        error: () => {
          /* Non-introducers / no record yet — leave the bell empty silently. */
        },
      });
  }

  markRead(id: number): Observable<void> {
    return this.httpClient
      .put<void>(`${this.url}/customer/account/notifications/${id}/read`, {})
      .pipe(tap(() => {
        const updated = this.$notifications.value.map((n) =>
          n.id === id ? { ...n, isRead: true } : n,
        );
        this.setNotifications(updated);
      }));
  }

  markAllRead(): Observable<void> {
    return this.httpClient
      .put<void>(`${this.url}/customer/account/notifications/read-all`, {})
      .pipe(tap(() => {
        const updated = this.$notifications.value.map((n) => ({ ...n, isRead: true }));
        this.setNotifications(updated);
      }));
  }

  private setNotifications(items: IClientNotification[]): void {
    this.$notifications.next(items);
    this.$unreadCount.next(items.filter((n) => !n.isRead).length);
  }
}
